// ============================================================
// TMR Portal Admin — percorrer o resultado filtrado inteiro, página a página
// ============================================================
import type { Paged } from '@tmr/api-client';
import type { ExportProgress } from './types';

/**
 * Hard ceiling on one export. Reached, the walk stops and says so instead of carrying on — a browser
 * building a spreadsheet of hundreds of thousands of rows stops responding, and an export nobody can
 * open is worse than one that admits it was truncated.
 */
export const MAX_EXPORT_ROWS = 50_000;

/** Thrown when the walk stops at {@link MAX_EXPORT_ROWS} with rows still unread. */
export class ExportTooLargeError extends Error {
  constructor(public readonly total: number) {
    super(
      `Esta exportação tem ${total.toLocaleString('pt-BR')} linhas, acima do limite de `
      + `${MAX_EXPORT_ROWS.toLocaleString('pt-BR')}. Restrinja os filtros e tente de novo.`,
    );
    this.name = 'ExportTooLargeError';
  }
}

/**
 * Walks a paged endpoint from the first page to the last, collecting every item.
 *
 * It follows `totalCount` rather than "until a page comes back short", because a short page is also what
 * the last page looks like when `totalCount` is an exact multiple of the page size — and stopping one
 * request early would silently drop the tail.
 *
 * @param fetchPage Reads one page; the caller has already bound the screen's filters into it.
 * @param onProgress Called after each page, so a button can count up while this runs.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<Paged<T>>,
  onProgress?: (progress: ExportProgress) => void,
): Promise<T[]> {
  const rows: T[] = [];
  let page = 1;
  let total = 0;

  for (;;) {
    const result = await fetchPage(page);
    total = result.totalCount;

    if (total > MAX_EXPORT_ROWS) throw new ExportTooLargeError(total);

    rows.push(...result.items);
    onProgress?.({ loaded: rows.length, total });

    // An empty page ends the walk whatever the count says: without this, a totalCount that disagreed with
    // reality — rows deleted while the walk is running — would loop for ever.
    if (result.items.length === 0 || rows.length >= total) return rows;

    page += 1;
  }
}
